import type { Activity, ActivityKind, Contact, ContactCreate, Customer, CustomerLocationPut, CrmTrend, CrmTrendKind, CustomerMetricsRow, CustomerPattern, CustomerStage, CustomerSummary } from '@morales/shared';
import { useQuickBooksHolds } from '../components/QuickBooksBand';
import { CustomerDetailsCard } from './CustomerForm';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth';
import { IconPhone, IconPlus } from '../components/icons';
import { CallAheadBadge, Empty, ErrorBox, InactiveBadge, PageHead, QueryView, SearchBox, TierBadge } from '../components/ui';
import { formatDate, formatMoney, formatPhone, formatStamp, normalizePhone, todayIso, weekdayName } from '../format';
import { useDebounced, useDesktop, useSearchParam, useTiers } from '../hooks';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { byNumber, byText, useSort } from '../components/useSort';
import { useLocale } from '../i18n';
import { parseNumber } from './StockPage';
import { qk } from '../queryClient';
import { CustomerTexts } from './TextsPage';

const STAGES: CustomerStage[] = ['prospect', 'trial', 'customer', 'lost'];
const KINDS: ActivityKind[] = ['visit', 'call', 'text', 'note', 'offer', 'task', 'complaint'];
const SELLS = new Set(['owner', 'office', 'sales']);

export function StageBadge({ stage }: { stage: CustomerStage }) {
  const { t } = useLocale();
  if (stage === 'customer') return null;
  return <span className={stage === 'lost' ? 'badge badge-danger' : 'badge badge-warn'}>{t(`stage_${stage}`)}</span>;
}

export function KindBadge({ kind }: { kind: ActivityKind }) {
  const { t } = useLocale();
  return <span className={kind === 'task' ? 'badge badge-ok' : 'badge'}>{t(`kind_${kind}`)}</span>;
}

/** Done / undo on a task. Refreshes the customer's history and the day's follow-ups. */
export function ActivityDoneButton({ activity, customerId, date }: { activity: Activity; customerId: string; date?: string }) {
  const { t } = useLocale();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (done: boolean) => api.setActivityDone(activity.id, done),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.activities(customerId) });
      void qc.invalidateQueries({ queryKey: qk.followups(date ?? todayIso()) });
    },
  });
  if (activity.kind !== 'task' && !activity.dueOn) return null;
  return (
    <button type="button" className={activity.doneAt ? 'btn btn-ghost' : 'btn btn-secondary'} onClick={() => m.mutate(!activity.doneAt)} disabled={m.isPending}>
      {activity.doneAt ? t('undo') : t('markDone')}
    </button>
  );
}

type CustomerLine = CustomerSummary & { m: CustomerMetricsRow | null; tr: CrmTrend | null };
/** The trends' order: the ones to act on first (decision 169: one trend on every screen) */
const TREND_RANK: CrmTrendKind[] = ['quiet', 'less', 'new', 'more', 'steady'];
/** The Trend column in the How they are ordering tab's order: gone quiet, less (the biggest dollar drop first), new,
 * more, steady; then the name. No trend goes last. */
const byTrend = (a: CustomerLine, b: CustomerLine): number => {
  if (!a.tr) return b.tr ? Number.POSITIVE_INFINITY : 0;
  if (!b.tr) return Number.NEGATIVE_INFINITY;
  const rank = TREND_RANK.indexOf(a.tr.trend) - TREND_RANK.indexOf(b.tr.trend);
  if (rank) return rank;
  const drop = a.tr.trend === 'less' ? a.tr.last4 - a.tr.prior4 - (b.tr.last4 - b.tr.prior4) : 0;
  return drop || a.name.localeCompare(b.name);
};
type CustomerSort = 'name' | 'tier' | 'stage' | 'sales30' | 'trend' | 'sales90' | 'gm' | 'last' | 'balance' | 'pastDue' | 'late';


/** `tabs`: Customers' tabs (decision 169), shown under the title. */
export function CustomersPage({ tabs }: { tabs?: ReactNode }) {
  const desktop = useDesktop();
  return desktop ? <CustomersSheet tabs={tabs} /> : <CustomersList tabs={tabs} />;
}

/** The office's customer sheet: one row per account with the numbers that say how it is doing. */
function CustomersSheet({ tabs }: { tabs?: ReactNode }) {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const navigate = useNavigate();
  const books = me?.role === 'owner' || me?.role === 'office';
  const [q, setQ] = useSearchParam('q');
  const [stageParam, setStageParam] = useSearchParam('stage');
  const stage = STAGES.find((s) => s === stageParam);
  const [activeOnly, setActiveOnly] = useState(true);
  // the customers put away (decision 184), for the owner and the office to bring back
  const [archivedParam, setArchived] = useSearchParam('archived');
  const archived = books && archivedParam === '1';
  const { nameOf } = useTiers();
  const term = useDebounced(q.trim());
  const list = useQuery({
    queryKey: qk.customers(term, stage ?? '', archived),
    queryFn: ({ signal }) => api.customers(term, signal, stage, archived),
    placeholderData: keepPreviousData,
  });
  const metrics = useQuery({ queryKey: ['reports', 'customer-metrics'], queryFn: ({ signal }) => api.customerMetrics(signal), enabled: books });
  const byId = useMemo(() => new Map((metrics.data?.rows ?? []).map((m) => [m.customerId, m])), [metrics.data]);
  // the trend is the one the trends tab shows (the last 4 weeks against the 4-week average of the 8 before), not a
  // second formula
  const today = todayIso();
  const trends = useQuery({ queryKey: qk.crmTrends(today), queryFn: ({ signal }) => api.crmTrends(today, signal), enabled: books });
  const trendOf = useMemo(() => new Map((trends.data?.rows ?? []).map((r) => [r.customerId, r])), [trends.data]);
  const lines: CustomerLine[] = useMemo(
    // an inactive account that still owes stays in view: its money is still out
    () =>
      (list.data ?? [])
        .map((c) => ({ ...c, m: byId.get(c.id) ?? null, tr: trendOf.get(c.id) ?? null }))
        .filter((c) => archived || !activeOnly || c.active || (c.balance ?? 0) !== 0 || (c.m?.pastDue ?? 0) > 0),
    [list.data, byId, trendOf, activeOnly, archived],
  );
  const sorters = useMemo(
    () => ({
      name: byText<CustomerLine>((c) => c.name),
      tier: byText<CustomerLine>((c) => nameOf(c.priceTier)),
      stage: byText<CustomerLine>((c) => t(`stage_${c.stage}`)),
      sales30: byNumber<CustomerLine>((c) => c.m?.sales30),
      trend: byTrend,
      sales90: byNumber<CustomerLine>((c) => c.m?.sales90),
      gm: byNumber<CustomerLine>((c) => c.m?.marginPct30),
      last: byText<CustomerLine>((c) => c.m?.lastInvoiceOn),
      balance: byNumber<CustomerLine>((c) => c.balance),
      pastDue: byNumber<CustomerLine>((c) => c.m?.pastDue),
      late: byNumber<CustomerLine>((c) => (c.m?.pastDue ? c.m.oldestDaysLate : null)),
    }),
    [nameOf, t],
  );
  // biggest sales first; without the money columns (sales) every row ties and the list keeps the name order
  const { sorted, head } = useSort<CustomerLine, CustomerSort>(sorters, { key: 'sales30', dir: -1 }, ['sales30', 'sales90', 'gm', 'last', 'balance', 'pastDue', 'late']);
  const shown = sorted(lines);
  const money = (v: number | null | undefined) => (v === null || v === undefined ? '' : formatMoney(v, intlLocale));
  const sum = (f: (c: CustomerLine) => number | null | undefined) => Math.round(shown.reduce((s, c) => s + (f(c) ?? 0), 0) * 100) / 100;
  const sales30 = sum((c) => c.m?.sales30);
  const prev30 = sum((c) => c.m?.salesPrev30);
  const margin30 = sum((c) => c.m?.margin30);
  const covered30 = sum((c) => c.m?.costedSales30);
  const balance = sum((c) => c.balance);
  const pastDue = sum((c) => c.m?.pastDue);
  const pct = (v: number | null) => (v === null ? '' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`);
  // until the numbers arrive (or when they fail) the strip says so instead of showing zeros
  const ready = metrics.data !== undefined;
  const sumOrDash = (v: number) => (ready ? money(v) : '—');

  const columns: SheetColumn<CustomerLine>[] = [
    {
      key: 'name',
      header: head('name', t('name')),
      label: t('name'),
      width: '180px',
      render: (c) => (
        <span>
          <Link to={`/customers/${c.id}`}>{c.name}</Link>
          {c.active ? null : <span className="cell-vendor"> · {t('inactive')}</span>}
          {c.onHold ? <span className="state state-overdue"> {t('onHold')}</span> : null}
        </span>
      ),
    },
    { key: 'tier', header: head('tier', t('tier')), label: t('tier'), width: '100px', render: (c) => nameOf(c.priceTier) },
    { key: 'stage', header: head('stage', t('stage')), label: t('stage'), width: '84px', render: (c) => t(`stage_${c.stage}`) },
    ...(books
      ? ([
          { key: 'sales30', header: head('sales30', t('sales30Col')), label: t('sales30Col'), width: '100px', align: 'right', render: (c) => (c.m?.sales30 ? <strong>{money(c.m.sales30)}</strong> : '') },
          {
            key: 'trend',
            header: head('trend', t('trendCol')),
            label: t('trendCol'),
            hint: t('trendHint'),
            width: '120px',
            render: (c) =>
              c.tr ? (
                <span title={c.tr.changePct === null ? undefined : pct(Math.round(c.tr.changePct * 10) / 10)}>
                  <span className={`trend-badge trend-${c.tr.trend}`}>{t(`trend_${c.tr.trend}`)}</span>
                </span>
              ) : (
                ''
              ),
          },
          { key: 'sales90', header: head('sales90', t('sales90Col')), label: t('sales90Col'), width: '100px', align: 'right', render: (c) => (c.m?.sales90 ? money(c.m.sales90) : '') },
          {
            key: 'gm',
            header: head('gm', t('gm30Col')),
            label: t('gm30Col'),
            width: '84px',
            align: 'right',
            render: (c) => (c.m?.marginPct30 === null || c.m?.marginPct30 === undefined ? '' : `${c.m.marginPct30.toFixed(1)}%`),
            cellClass: (c) => (c.m?.marginPct30 !== null && c.m?.marginPct30 !== undefined && c.m.marginPct30 < 0 ? 'bad' : undefined),
          },
          { key: 'last', header: head('last', t('lastInvoiceCol')), label: t('lastInvoiceCol'), width: '100px', render: (c) => (c.m?.lastInvoiceOn ? formatDate(c.m.lastInvoiceOn, intlLocale, 'date') : '') },
        ] satisfies SheetColumn<CustomerLine>[])
      : []),
    { key: 'balance', header: head('balance', t('balance')), label: t('balance'), width: '100px', align: 'right', render: (c) => (c.balance === null ? '—' : money(c.balance)) },
    ...(books
      ? ([
          { key: 'pastDue', header: head('pastDue', t('pastDueCol')), label: t('pastDueCol'), width: '96px', align: 'right', render: (c) => (c.m?.pastDue ? money(c.m.pastDue) : ''), cellClass: (c) => (c.m?.pastDue ? 'bad' : undefined) },
          { key: 'late', header: head('late', t('daysLateCol')), label: t('daysLateCol'), width: '72px', align: 'right', render: (c) => (c.m?.pastDue && c.m.oldestDaysLate ? String(c.m.oldestDaysLate) : '') },
        ] satisfies SheetColumn<CustomerLine>[])
      : []),
  ];

  return (
    <div className="desk-page">
      <header className="desk-head">
        <div>
          <h1>{t('customers')}</h1>
          <p className="muted">{t('customersHint')}</p>
        </div>
        {SELLS.has(me?.role ?? '') ? (
          <Link to="/customers/new" className="btn btn-primary">
            + {t('newCustomer')}
          </Link>
        ) : null}
      </header>
      {tabs}
      <div className="toolbar">
        <input className="tool-search" type="search" placeholder={t('search')} aria-label={t('search')} value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="seg" role="group" aria-label={t('stage')}>
          <button type="button" aria-pressed={!stage} onClick={() => setStageParam('')}>
            {t('allStages')}
          </button>
          {STAGES.map((st) => (
            <button key={st} type="button" aria-pressed={stage === st} onClick={() => setStageParam(st)}>
              {t(`stage_${st}`)}
            </button>
          ))}
        </div>
        <label className="tool check">
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
          {t('activeOnly')}
        </label>
        {books ? (
          <label className="tool check">
            <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked ? '1' : '')} />
            {t('archivedFilter')}
          </label>
        ) : null}
      </div>
      <dl className="totals-strip" data-testid="customer-totals">
        <div>
          <dt>{t('customersCol')}</dt>
          <dd>{shown.length}</dd>
        </div>
        {books ? (
          <>
            <div>
              <dt>{t('sales30Col')}</dt>
              <dd>{sumOrDash(sales30)}</dd>
            </div>
            <div>
              <dt>{t('vsPrior30')}</dt>
              <dd>{ready && prev30 > 0 ? pct(Math.round(((sales30 - prev30) / prev30) * 1000) / 10) : '—'}</dd>
            </div>
            <div>
              <dt>{t('gm30Col')}</dt>
              <dd>{ready && covered30 > 0 ? `${((margin30 / covered30) * 100).toFixed(1)}%` : '—'}</dd>
            </div>
          </>
        ) : null}
        <div>
          <dt>{t('balance')}</dt>
          <dd>{money(balance)}</dd>
        </div>
        {books ? (
          <div className={pastDue > 0 ? 'bad' : undefined}>
            <dt>{t('pastDueCol')}</dt>
            <dd>{sumOrDash(pastDue)}</dd>
          </div>
        ) : null}
      </dl>
      {metrics.isError ? <ErrorBox error={metrics.error} onRetry={() => void metrics.refetch()} /> : null}
      <QueryView query={list}>
        {() =>
          shown.length === 0 ? (
            <p className="muted pick-first">{t('noResults')}</p>
          ) : (
            <Sheet label={t('customers')} columns={columns} rows={shown} rowKey={(c) => c.id} rowLabel={(c) => c.name} stickyFirst minWidth={books ? '1080px' : '640px'} onOpen={(c) => navigate(`/customers/${c.id}`)} rowClass={(c) => (c.active ? undefined : 'zero')} />
          )
        }
      </QueryView>
    </div>
  );
}

/** The phone's list: big rows, the balance on the right. */
function CustomersList({ tabs }: { tabs?: ReactNode }) {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const [q, setQ] = useSearchParam('q');
  const [stageParam, setStageParam] = useSearchParam('stage');
  const stage = STAGES.find((s) => s === stageParam);
  const term = useDebounced(q.trim());
  const list = useQuery({
    queryKey: qk.customers(term, stage ?? ''),
    queryFn: ({ signal }) => api.customers(term, signal, stage),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="stack">
      <h1>{t('customers')}</h1>
      {tabs}
      {SELLS.has(me?.role ?? '') ? (
        <Link to="/customers/new" className="btn btn-primary btn-lg btn-block">
          <IconPlus /> {t('newCustomer')}
        </Link>
      ) : null}
      <SearchBox value={q} onChange={setQ} />
      <div className="chips" role="group" aria-label={t('stage')}>
        <button type="button" className="chip" aria-pressed={!stage} onClick={() => setStageParam('')}>
          {t('allStages')}
        </button>
        {STAGES.map((st) => (
          <button key={st} type="button" className="chip" aria-pressed={stage === st} onClick={() => setStageParam(st)}>
            {t(`stage_${st}`)}
          </button>
        ))}
      </div>
      <QueryView query={list}>
        {(customers) =>
          customers.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list">
              {customers.map((c) => (
                <li key={c.id}>
                  <Link to={`/customers/${c.id}`} className="row">
                    <span className="row-main">
                      <span className="row-title">{c.name}</span>
                      <span className="row-sub">
                        <TierBadge tier={c.priceTier} />
                        <StageBadge stage={c.stage} />
                        {c.callAhead ? <CallAheadBadge /> : null}
                        {c.active ? null : <InactiveBadge />}
                      </span>
                    </span>
                    <span className="row-end">
                      <span className="row-end-label">{t('balance')}</span>
                      <span className={c.balance !== null && c.balance > 0 ? 'row-end-value owed' : 'row-end-value'}>
                        {c.balance === null ? '—' : formatMoney(c.balance, intlLocale)}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
    </div>
  );
}

export function CustomerDetailPage() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: qk.customer(id), queryFn: ({ signal }) => api.customer(id, signal) });
  const sells = SELLS.has(me?.role ?? '');
  const stageM = useMutation({ mutationFn: (stage: CustomerStage) => api.patchCustomer(id, { stage }), onSuccess: (c) => qc.setQueryData(qk.customer(id), c) });
  // the language the customer's invoices, statements, portal and texts are in (English unless set to Spanish here)
  const langM = useMutation({ mutationFn: (language: 'en' | 'es') => api.patchCustomer(id, { language }), onSuccess: (c) => qc.setQueryData(qk.customer(id), c) });
  // the price tier: the owner's and the office's to change (decision 167); their orders not invoiced take its prices
  const setsMoney = me?.role === 'owner' || me?.role === 'office';
  const holds = useQuickBooksHolds();
  const tiers = useQuery({ queryKey: qk.priceTiers, queryFn: ({ signal }) => api.priceTiers(signal), enabled: setsMoney, staleTime: 10 * 60_000 });
  const tierM = useMutation({
    mutationFn: (priceTier: Customer['priceTier']) => api.patchCustomer(id, { priceTier }),
    onSuccess: (c) => {
      qc.setQueryData(qk.customer(id), c);
      void qc.invalidateQueries({ queryKey: qk.orderLists });
    },
  });

  return (
    <QueryView query={query}>
      {(c) => (
        <div className="stack">
          <PageHead title={c.name} back="/customers" />
          <div className="badges">
            <TierBadge tier={c.priceTier} />
            <StageBadge stage={c.stage} />
            {c.onHold ? <span className="badge badge-danger">{t('onHold')}</span> : null}
            {c.callAhead ? <CallAheadBadge /> : null}
            {c.active ? null : <InactiveBadge />}
          </div>
          {sells && !c.archived ? (
            <label className="field">
              <span>{t('stage')}</span>
              <select value={c.stage} aria-label={t('stage')} onChange={(e) => stageM.mutate(e.target.value as CustomerStage)} disabled={stageM.isPending}>
                {STAGES.map((st) => (
                  <option key={st} value={st}>
                    {t(`stage_${st}`)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {stageM.error ? <ErrorBox error={stageM.error} /> : null}

          <CustomerDetailsCard key={c.id} customer={c} sells={sells} setsMoney={setsMoney} />
          {/* a customer put away takes no new orders until brought back */}
          {!c.archived ? (
            <>
              <Link to={`/orders/new?customer=${c.id}`} className="btn btn-primary btn-lg btn-block">
                <IconPlus /> {t('newOrder')}
              </Link>
              <Link to={`/customers/${c.id}/standing`} className="btn btn-secondary btn-lg btn-block">
                {t('standingOrder')}
              </Link>
            </>
          ) : null}

          <Link to={`/customers/${c.id}/statement`} className="card balance-card">
            <span className="muted">{t('balance')}</span>
            <strong className={c.balance !== null && c.balance > 0 ? 'owed' : undefined}>
              {c.balance === null ? '—' : formatMoney(c.balance, intlLocale)}
            </strong>
            {c.balance === null ? <span className="muted">{t('balancePending')}</span> : null}
          </Link>
          {/* practice (decision 187): its invoices and money are on the statement's Practice view */}
          {holds ? (
            <Link to={`/customers/${c.id}/statement?practice=1`} className="btn btn-ghost" data-testid="practice-statement">
              {t('practiceStatement')}
            </Link>
          ) : null}

          <dl className="facts">
            {setsMoney && !c.archived ? (
              <div className="fact">
                <dt>{t('priceTierLabel')}</dt>
                <dd>
                  <select
                    aria-label={t('priceTierLabel')}
                    value={c.priceTier}
                    disabled={tierM.isPending}
                    onChange={(e) => {
                      const next = e.target.value as Customer['priceTier'];
                      const name = tiers.data?.find((x) => x.code === next)?.name ?? next;
                      if (next !== c.priceTier && window.confirm(t('priceTierConfirm').replace('{name}', c.name).replace('{tier}', name))) tierM.mutate(next);
                    }}
                  >
                    {(tiers.data ?? [{ code: c.priceTier, name: c.priceTier, active: true }]).filter((x) => x.active || x.code === c.priceTier).map((x) => (
                      <option key={x.code} value={x.code}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                  {tierM.error ? <ErrorBox error={tierM.error} /> : null}
                </dd>
              </div>
            ) : null}
            <div className="fact">
              <dt>{t('terms')}</dt>
              <dd>
                {c.termsDays} {t('days')}
              </dd>
            </div>
            <div className="fact">
              <dt>{t('deliveryDays')}</dt>
              <dd>{c.deliveryDays.length ? c.deliveryDays.map((d) => weekdayName(d, intlLocale)).join(', ') : '—'}</dd>
            </div>
            <div className="fact">
              <dt>{t('language')}</dt>
              <dd>
                {sells && !c.archived ? (
                  <select aria-label={t('language')} value={c.language} disabled={langM.isPending} onChange={(e) => langM.mutate(e.target.value === 'es' ? 'es' : 'en')}>
                    <option value="en">{t('lang_en')}</option>
                    <option value="es">{t('lang_es')}</option>
                  </select>
                ) : (
                  t(`lang_${c.language}`)
                )}
                {langM.error ? <ErrorBox error={langM.error} /> : null}
              </dd>
            </div>
            {c.customerType ? (
              <div className="fact">
                <dt>{t('category')}</dt>
                <dd>{c.customerType}</dd>
              </div>
            ) : null}
            {c.source ? (
              <div className="fact">
                <dt>{t('source')}</dt>
                <dd>{c.source}</dd>
              </div>
            ) : null}
          </dl>

          <DeliveryCard customer={c} canEdit={sells && !c.archived} />
          {sells ? <PatternCard customer={c} canEdit={(me?.role === 'owner' || me?.role === 'office') && !c.archived} /> : null}
          {sells ? <SuggestionsCard customerId={c.id} /> : null}
          {(me?.role === 'owner' || me?.role === 'office') && !c.archived ? <CreditBox customer={c} /> : null}
          <Activities customerId={c.id} canWrite={sells && !c.archived} />

          {c.notes ? (
            <section className="card">
              <h2>{t('notes')}</h2>
              <p className="pre">{c.notes}</p>
            </section>
          ) : null}

          <h2>{t('contacts')}</h2>
          <Contacts customer={c} canWrite={sells && !c.archived} />
          {sells ? <CustomerTexts customerId={c.id} contacts={c.contacts} /> : null}

          <h2>{t('recentInvoices')}</h2>
          {c.recentInvoices.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list">
              {c.recentInvoices.map((inv) => (
                <li key={inv.id} className="row">
                  <span className="row-main">
                    <span className="row-title">
                      {t('invoice')} {inv.invoiceNo}
                    </span>
                    <span className="row-sub">{formatDate(inv.date, intlLocale, 'date')}</span>
                  </span>
                  <span className="row-end">
                    <span className="row-end-value">{formatMoney(inv.total, intlLocale)}</span>
                    {inv.balance !== null && inv.balance !== 0 ? (
                      <span className={inv.balance > 0 ? 'row-end-label owed' : 'row-end-label'}>
                        {t('balance')} {formatMoney(inv.balance, intlLocale)}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </QueryView>
  );
}

/** What their peers (same price tier) buy that they have not in 90 days (decision 111): ideas for the next call. */
function SuggestionsCard({ customerId }: { customerId: string }) {
  const { t, intlLocale } = useLocale();
  const query = useQuery({ queryKey: qk.suggestions(customerId), queryFn: ({ signal }) => api.suggestions(customerId, signal), staleTime: 10 * 60_000 });
  if (!query.data || query.data.length === 0) return null;
  return (
    <section className="card stack-sm" data-testid="suggestions">
      <h2>{t('suggestionsTitle')}</h2>
      <p className="muted">{t('suggestionsHint')}</p>
      <ul className="list">
        {query.data.map((s) => (
          <li key={s.itemId}>
            <Link className="row" to={`/items/${s.itemId}`}>
              <span className="row-main">
                <span className="row-title">{s.itemName}</span>
                <span className="row-sub">
                  {t('suggestionPeers').replace('{n}', String(s.peers)).replace('{of}', String(s.of))}
                  {s.lastBought ? ` · ${t('lastBoughtOn')} ${formatDate(s.lastBought, intlLocale, 'date')}` : ` · ${t('neverBought')}`}
                </span>
              </span>
              <span className="row-end-value">{s.price === null ? '—' : formatMoney(s.price, intlLocale)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** What happened with the customer (visits, calls, notes, offers, tasks) and the form to log the next one. */
export function Activities({ customerId, canWrite }: { customerId: string; canWrite: boolean }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: qk.activities(customerId), queryFn: ({ signal }) => api.activities(customerId, signal) });
  const [kind, setKind] = useState<ActivityKind>('visit');
  const [body, setBody] = useState('');
  const [dueOn, setDueOn] = useState('');
  const add = useMutation({
    mutationFn: () => api.addActivity(customerId, { kind, body: body.trim(), ...(dueOn ? { dueOn } : {}) }),
    onSuccess: () => {
      setBody('');
      setDueOn('');
      void qc.invalidateQueries({ queryKey: qk.activities(customerId) });
      void qc.invalidateQueries({ queryKey: ['crm'] });
    },
  });
  return (
    <section className="card stack-sm">
      <h2>{t('activities')}</h2>
      {canWrite ? (
        <form
          className="stack-sm"
          onSubmit={(e) => {
            e.preventDefault();
            if (body.trim()) add.mutate();
          }}
        >
          <div className="chips" role="group" aria-label={t('kind')}>
            {KINDS.map((k) => (
              <button key={k} type="button" className="chip" aria-pressed={kind === k} onClick={() => setKind(k)}>
                {t(`kind_${k}`)}
              </button>
            ))}
          </div>
          <label className="field">
            <span>{t('whatHappened')}</span>
            <textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('dueOn')}</span>
            <input type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
          </label>
          {add.error ? <ErrorBox error={add.error} /> : null}
          <button type="submit" className="btn btn-primary btn-block" disabled={add.isPending || !body.trim()}>
            {add.isPending ? t('sending') : t('logActivity')}
          </button>
        </form>
      ) : null}
      <QueryView query={list}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list" data-testid="activities">
              {rows.map((a) => (
                <li key={a.id} className={a.doneAt ? 'row muted' : 'row'}>
                  <span className="row-main">
                    <span className="row-title">
                      <KindBadge kind={a.kind} /> {a.body}
                    </span>
                    <span className="row-sub">
                      {formatStamp(a.happenedAt, intlLocale, 'date')}
                      {a.byName ? ` · ${a.byName}` : ''}
                      {a.dueOn ? ` · ${t('dueOn')} ${formatDate(a.dueOn, intlLocale, 'day')}` : ''}
                      {a.doneAt ? ` · ${t('doneLabel')}` : ''}
                    </span>
                  </span>
                  {canWrite ? <ActivityDoneButton activity={a} customerId={customerId} /> : null}
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
    </section>
  );
}

const blankContact = (): ContactCreate => ({ name: '', whatsapp: false, smsConsent: false, preferred: false, getsInvoices: false });

/** Contacts with call / WhatsApp buttons; sales, office and owner add and edit them (birthday, what they like, consent). */
export function Contacts({ customer, canWrite }: { customer: Customer; canWrite: boolean }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<'new' | string | null>(null);
  const [form, setForm] = useState<ContactCreate & { phoneText: string; consentNote: string; portalAccess: boolean; emailText: string }>({ ...blankContact(), phoneText: '', consentNote: '', portalAccess: false, emailText: '' });
  const start = (ct?: Contact) => {
    setEditing(ct ? ct.id : 'new');
    setForm({
      name: ct?.name ?? '',
      ...(ct?.role ? { role: ct.role } : {}),
      whatsapp: ct?.whatsapp ?? false,
      smsConsent: ct?.smsConsent ?? false,
      preferred: ct?.preferred ?? false,
      ...(ct?.birthday ? { birthday: ct.birthday } : {}),
      ...(ct?.likes ? { likes: ct.likes } : {}),
      phoneText: ct?.phone ? formatPhone(ct.phone) : '',
      consentNote: '',
      portalAccess: ct?.portalAccess ?? false,
      emailText: ct?.email ?? '',
      getsInvoices: ct?.getsInvoices ?? false,
    });
  };
  const save = useMutation({
    mutationFn: async () => {
      const phone = form.phoneText.trim() ? normalizePhone(form.phoneText) : null;
      if (form.phoneText.trim() && !phone) throw new Error(t('phone'));
      const email = form.emailText.trim().toLowerCase() || null;
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error(t('email'));
      const body = {
        getsInvoices: form.getsInvoices && !!email,
        name: form.name.trim(),
        role: form.role?.trim() ?? '',
        whatsapp: form.whatsapp,
        smsConsent: form.smsConsent,
        preferred: form.preferred,
        ...(form.consentNote.trim() ? { consentNote: form.consentNote.trim() } : {}),
        likes: form.likes?.trim() ?? '',
      };
      if (editing === 'new') {
        const made = await api.addContact(customer.id, { ...body, ...(phone ? { phone } : {}), ...(email ? { email } : {}), ...(form.birthday ? { birthday: form.birthday } : {}) });
        return form.portalAccess ? api.patchContact(made.id, { portalAccess: true }) : made;
      }
      return api.patchContact(editing!, { ...body, phone, email, birthday: form.birthday || null, portalAccess: form.portalAccess });
    },
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: qk.customer(customer.id) });
    },
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <>
      {customer.contacts.length === 0 ? (
        <Empty>{t('noResults')}</Empty>
      ) : (
        <ul className="list">
          {customer.contacts.map((ct) => (
            <li key={ct.id} className="row contact">
              <span className="row-main">
                <span className="row-title">
                  {ct.name ?? '—'} {ct.preferred ? <span className="badge badge-ok">{t('preferred')}</span> : null}
                </span>
                {ct.role ? <span className="row-sub">{ct.role}</span> : null}
                {ct.phone ? <span className="row-sub">{formatPhone(ct.phone)}</span> : null}
                {ct.email ? (
                  <span className="row-sub">
                    {ct.email} {ct.getsInvoices ? <span className="badge">{t('getsInvoicesBadge')}</span> : null}
                  </span>
                ) : null}
                {ct.birthday ? (
                  <span className="row-sub">
                    {t('birthday')} {formatDate(ct.birthday, intlLocale, 'monthDay')}
                  </span>
                ) : null}
                {ct.likes ? <span className="row-sub">{ct.likes}</span> : null}
                {ct.portalAccess ? (
                  <span className="row-sub">
                    <span className="badge badge-ok">{t('portalAccess')}</span>
                  </span>
                ) : null}
              </span>
              <span className="contact-actions">
                {ct.phone ? (
                  <a className="btn btn-secondary" href={`tel:${ct.phone}`}>
                    <IconPhone /> {t('call')}
                  </a>
                ) : null}
                {ct.phone && ct.whatsapp ? (
                  <a className="btn btn-secondary" href={`https://wa.me/${ct.phone.slice(1)}`} target="_blank" rel="noreferrer">
                    {t('channel_whatsapp')}
                  </a>
                ) : null}
                {canWrite ? (
                  <button type="button" className="btn btn-ghost" onClick={() => start(ct)} aria-label={`${t('editContact')} ${ct.name ?? ''}`}>
                    {t('editContact')}
                  </button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      {canWrite && editing === null ? (
        <button type="button" className="btn btn-secondary btn-block" onClick={() => start()}>
          <IconPlus /> {t('addContact')}
        </button>
      ) : null}
      {editing !== null ? (
        <form
          className="card stack-sm"
          onSubmit={(e) => {
            e.preventDefault();
            if (form.name.trim()) save.mutate();
          }}
        >
          <label className="field">
            <span>{t('contactName')}</span>
            <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} />
          </label>
          <label className="field">
            <span>{t('contactRole')}</span>
            <input type="text" value={form.role ?? ''} onChange={(e) => set('role', e.target.value)} />
          </label>
          <label className="field">
            <span>{t('phone')}</span>
            <input type="tel" value={form.phoneText} onChange={(e) => set('phoneText', e.target.value)} />
          </label>
          <label className="field">
            <span>{t('email')}</span>
            <input type="email" autoComplete="off" value={form.emailText} onChange={(e) => set('emailText', e.target.value)} />
          </label>
          <label className="check">
            <input type="checkbox" checked={!!form.getsInvoices} disabled={!form.emailText.trim()} onChange={(e) => set('getsInvoices', e.target.checked)} /> {t('getsInvoicesLabel')}
          </label>
          <label className="field">
            <span>{t('birthday')}</span>
            <input type="date" value={form.birthday ?? ''} onChange={(e) => set('birthday', e.target.value)} />
          </label>
          <label className="field">
            <span>{t('likes')}</span>
            <textarea rows={2} value={form.likes ?? ''} onChange={(e) => set('likes', e.target.value)} />
          </label>
          <label className="check">
            <input type="checkbox" checked={form.whatsapp} onChange={(e) => set('whatsapp', e.target.checked)} /> {t('whatsappLabel')}
          </label>
          <label className="check">
            <input type="checkbox" checked={form.preferred} onChange={(e) => set('preferred', e.target.checked)} /> {t('preferred')}
          </label>
          <label className="check">
            <input type="checkbox" checked={form.smsConsent} onChange={(e) => set('smsConsent', e.target.checked)} /> {t('smsConsentLabel')}
          </label>
          <label className="check">
            <input type="checkbox" checked={form.portalAccess} onChange={(e) => set('portalAccess', e.target.checked)} /> {t('portalAccess')}
          </label>
          {form.portalAccess ? <p className="muted">{t('portalAccessHint')}</p> : null}
          {form.smsConsent ? (
            <label className="field">
              <span>{t('consentNote')}</span>
              <input type="text" value={form.consentNote} onChange={(e) => set('consentNote', e.target.value)} placeholder={t('consentNotePlaceholder')} />
            </label>
          ) : null}
          {save.error ? <ErrorBox error={save.error} /> : null}
          <div className="quick-grid">
            <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
              {t('cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={save.isPending || !form.name.trim()}>
              {t('save')}
            </button>
          </div>
        </form>
      ) : null}
    </>
  );
}

/** The credit limit and the hold (owner/office): the rule every order path enforces. */
/** Where the truck goes: the address, the window and the driver's instructions; the driver sees it on the stop (decision 53). */
function DeliveryCard({ customer, canEdit }: { customer: Customer; canEdit: boolean }) {
  const { t } = useLocale();
  const qc = useQueryClient();
  const loc = customer.location;
  const [form, setForm] = useState({
    addressLine1: loc?.addressLine1 ?? '', addressLine2: loc?.addressLine2 ?? '', city: loc?.city ?? '', state: loc?.state ?? 'NM', zip: loc?.zip ?? '',
    windowStart: loc?.windowStart ?? '', windowEnd: loc?.windowEnd ?? '', deliveryNotes: loc?.deliveryNotes ?? '', routeName: loc?.routeName ?? '',
  });
  const save = useMutation({
    mutationFn: (body: CustomerLocationPut) => api.putCustomerLocation(customer.id, body),
    onSuccess: (c) => {
      qc.setQueryData(qk.customer(customer.id), c);
      void qc.invalidateQueries({ queryKey: ['routes'] });
    },
  });
  const address = loc ? [loc.addressLine1, loc.addressLine2, [loc.city, loc.state].filter(Boolean).join(', '), loc.zip].filter((x) => x && x.trim()).join(', ') : '';
  const field = (key: keyof typeof form, label: string, extra: Partial<React.InputHTMLAttributes<HTMLInputElement>> = {}) => (
    <label className="field">
      <span>{label}</span>
      <input
        type="text"
        autoComplete="off"
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        onBlur={() => {
          const v = form[key].trim();
          const before = (loc?.[key] ?? (key === 'state' ? 'NM' : '')) ?? '';
          if (v !== before) save.mutate({ [key]: v || null });
        }}
        {...extra}
      />
    </label>
  );
  return (
    <section className="card stack-sm" data-testid="delivery">
      <h2>{t('deliveryTitle')}</h2>
      {address ? (
        <p>
          <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`} target="_blank" rel="noreferrer">
            {address}
          </a>
        </p>
      ) : (
        <p className="muted">{t('noAddress')}</p>
      )}
      {canEdit ? (
        <>
          <p className="muted">{t('deliveryHint')}</p>
          {field('addressLine1', t('addressLine1'))}
          {field('addressLine2', t('addressLine2'))}
          <div className="contact-actions">
            {field('city', t('city'))}
            {field('state', t('stateCode'), { maxLength: 2, style: { width: 56 } })}
            {field('zip', t('zip'), { inputMode: 'numeric', style: { width: 90 } })}
          </div>
          <div className="contact-actions">
            {field('windowStart', t('windowStart'), { type: 'time', style: { width: 120 } })}
            {field('windowEnd', t('windowEnd'), { type: 'time', style: { width: 120 } })}
            {field('routeName', t('usualRoute'), { maxLength: 40, placeholder: 'Route 1', title: t('usualRouteHint'), style: { width: 140 } })}
          </div>
          <label className="field">
            <span>{t('deliveryNotes')}</span>
            <textarea rows={2} value={form.deliveryNotes} onChange={(e) => setForm({ ...form, deliveryNotes: e.target.value })} onBlur={() => { const v = form.deliveryNotes.trim(); if (v !== (loc?.deliveryNotes ?? '')) save.mutate({ deliveryNotes: v || null }); }} />
          </label>
          {save.error ? <ErrorBox error={save.error} /> : null}
        </>
      ) : loc?.deliveryNotes ? (
        <p className="pre">{loc.deliveryNotes}</p>
      ) : null}
    </section>
  );
}

/** What the customer buys, from the invoices (QuickBooks' too) and open orders: the usual days and items, when the next order is expected (decisions 52, 152). */
function PatternCard({ customer, canEdit }: { customer: Customer; canEdit: boolean }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: qk.pattern(customer.id), queryFn: ({ signal }) => api.customerPattern(customer.id, signal) });
  const [sort, setSort] = useState(String(customer.keyAccountSort ?? 100));
  const loads = useQuery({ queryKey: qk.orderDays, queryFn: ({ signal }) => api.orderDays(signal), staleTime: 10 * 60_000, enabled: canEdit && customer.keyAccount });
  const onLoads = customer.keyAccountLoads ?? null;
  const save = useMutation({
    mutationFn: (body: { keyAccount?: boolean; keyAccountSort?: number; keyAccountLoads?: number[] }) => api.patchCustomer(customer.id, body),
    onSuccess: (c) => {
      qc.setQueryData(qk.customer(customer.id), c);
      void qc.invalidateQueries({ queryKey: ['customers', 'list'] });
      void qc.invalidateQueries({ queryKey: ['forecast'] });
    },
  });
  const statusClass: Record<CustomerPattern['status'], string> = { new: 'badge', ok: 'badge badge-good', due: 'badge badge-warn', overdue: 'badge badge-danger', quiet: 'badge badge-danger' };
  return (
    <section className="card stack-sm" data-testid="pattern">
      <h2>{t('patternTitle')}</h2>
      <QueryView query={query}>
        {(p) => (
          <>
            <div className="badges">
              <span className={statusClass[p.status]}>{t(`pattern_${p.status}`)}</span>
              {p.orders > 0 ? (
                <span className="muted">
                  {p.orders} {t('orders').toLowerCase()} · {p.ordersPerWeek} {t('patternPerWeek')}
                </span>
              ) : null}
            </div>
            <dl className="facts">
              <div className="fact">
                <dt>{t('patternOrdersOn')}</dt>
                <dd>{p.usualDays.length ? p.usualDays.map((d) => weekdayName(d, intlLocale)).join(', ') : t('patternNoDays')}</dd>
              </div>
              <div className="fact">
                <dt>{t('lastOrder')}</dt>
                <dd>{p.lastOrderOn ? formatDate(p.lastOrderOn, intlLocale, 'date') : '—'}</dd>
              </div>
              <div className="fact">
                <dt>{t('nextExpected')}</dt>
                <dd>{p.nextExpectedOn ? formatDate(p.nextExpectedOn, intlLocale, 'date') : '—'}</dd>
              </div>
              {p.typicalTotal !== null ? (
                <div className="fact">
                  <dt>{t('typicalOrder')}</dt>
                  <dd>{formatMoney(p.typicalTotal, intlLocale)}</dd>
                </div>
              ) : null}
            </dl>
            {p.byWeekday.some((w) => w.orders > 0) ? (
              <ol className="weekday-bars" aria-label={t('patternOrdersOn')}>
                {p.byWeekday.map((w) => (
                  <li key={w.weekday} className={p.usualDays.includes(w.weekday) ? 'usual' : undefined} title={`${Math.round(w.share * 100)}%`}>
                    <span className="wd-bar" style={{ height: `${Math.max(2, Math.round(w.share * 40))}px` }} />
                    <span className="day">{weekdayName(w.weekday, intlLocale).slice(0, 2)}</span>
                  </li>
                ))}
              </ol>
            ) : null}
            {p.items.length > 0 ? (
              <>
                <h3>{t('usuallyTakes')}</h3>
                <ul className="list">
                  {p.items.map((it) => (
                    <li key={it.itemId} className="row">
                      <span className="row-main">
                        <span className="row-title">{it.itemName}</span>
                        <span className="row-sub">
                          {it.timesOrdered}/{it.ofOrders} · {t('lastOrder').toLowerCase()} {it.lastQty} {formatDate(it.lastOn, intlLocale, 'date')}
                        </span>
                      </span>
                      <span className="row-end">
                        <span className="row-end-value">{it.typicalQty}</span>
                        <span className="row-end-label">{it.unit}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        )}
      </QueryView>
      {canEdit ? (
        <>
          <label className="check">
            <input type="checkbox" checked={customer.keyAccount} onChange={(e) => save.mutate({ keyAccount: e.target.checked })} /> {t('keyAccount')}
          </label>
          {customer.keyAccount && loads.data ? (
            <fieldset className="check-row">
              <legend>{t('columnOnLoads')}</legend>
              {loads.data.map((l) => (
                <label key={l.weekday} className="check">
                  <input
                    type="checkbox"
                    checked={onLoads === null || onLoads.includes(l.weekday)}
                    onChange={(e) => {
                      const now = new Set(onLoads ?? loads.data.map((x) => x.weekday));
                      if (e.target.checked) now.add(l.weekday);
                      else now.delete(l.weekday);
                      if (now.size === 0) return; // a column on no load is no column: untick key account instead
                      // every load ticked is the same as none set
                      save.mutate({ keyAccountLoads: loads.data.every((x) => now.has(x.weekday)) ? [] : [...now].sort() });
                    }}
                  />{' '}
                  {l.name}
                </label>
              ))}
            </fieldset>
          ) : null}
          {customer.keyAccount ? (
            <label className="field">
              <span>{t('columnOrder')}</span>
              <input
                type="text"
                inputMode="numeric"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                onBlur={() => {
                  const v = Number(sort);
                  if (!Number.isInteger(v) || v < 0 || v > 1000) return;
                  if (v !== (customer.keyAccountSort ?? 100)) save.mutate({ keyAccountSort: v });
                }}
              />
            </label>
          ) : null}
          {save.error ? <ErrorBox error={save.error} /> : null}
        </>
      ) : null}
    </section>
  );
}

function CreditBox({ customer }: { customer: Customer }) {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  // a credit limit is the owner's alone; the office still holds and unholds (decision 181)
  const setsLimit = me?.role === 'owner';
  const qc = useQueryClient();
  const [limit, setLimit] = useState(customer.creditLimit === null ? '' : String(customer.creditLimit));
  const [reason, setReason] = useState(customer.holdReason ?? '');
  const [terms, setTerms] = useState(String(customer.termsDays));
  const save = useMutation({
    mutationFn: (body: { creditLimit?: number | null; onHold?: boolean; holdReason?: string | null; termsDays?: number; termsAgreedOn?: string | null }) => api.patchCustomer(customer.id, body),
    onSuccess: (c) => {
      qc.setQueryData(qk.customer(customer.id), c);
      void qc.invalidateQueries({ queryKey: ['customers', 'list'] });
    },
  });
  return (
    <section className="card stack-sm">
      <h2>{t('creditTitle')}</h2>
      <p className="muted">{t('creditHint')}</p>
      <label className="field">
        <span>{t('creditLimit')}</span>
        <input
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder={t('noLimit')}
          value={limit}
          readOnly={!setsLimit}
          title={setsLimit ? undefined : t('limitOwnerOnly')}
          onChange={(e) => setLimit(e.target.value)}
          aria-invalid={(limit.trim() !== '' && parseNumber(limit) === null) || undefined}
          onBlur={() => {
            if (!setsLimit) return;
            const v = limit.trim() === '' ? null : parseNumber(limit);
            if (limit.trim() !== '' && v === null) return; // shown in red below, never saved as something else
            if (v !== customer.creditLimit) save.mutate({ creditLimit: v });
          }}
        />
        {limit.trim() !== '' && parseNumber(limit) === null ? <small className="bad">{t('typedNotNumber')}</small> : null}
        {setsLimit ? null : <small className="muted">{t('limitOwnerOnly')}</small>}
      </label>
      <div className="contact-actions">
        <label className="field">
          <span>{t('termsDaysLabel')}</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={terms}
            aria-label={t('termsDaysLabel')}
            style={{ width: 70 }}
            onChange={(e) => setTerms(e.target.value)}
            onBlur={() => {
              const v = Number(terms);
              if (!Number.isInteger(v) || v < 0 || v > 30) return;
              if (v !== customer.termsDays) save.mutate({ termsDays: v });
            }}
          />
        </label>
        <label className="field">
          <span>{t('termsAgreedOn')}</span>
          <input type="date" value={customer.termsAgreedOn ?? ''} aria-label={t('termsAgreedOn')} onChange={(e) => save.mutate({ termsAgreedOn: e.target.value || null })} />
        </label>
      </div>
      {Number(terms) > 30 ? <p className="field-error">{t('pacaTermsRule')}</p> : null}
      <label className="check">
        <input type="checkbox" checked={customer.onHold} onChange={(e) => save.mutate({ onHold: e.target.checked, holdReason: e.target.checked ? reason.trim() || null : null })} /> {t('onHold')}
      </label>
      {customer.onHold ? (
        <label className="field">
          <span>{t('holdReason')}</span>
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} onBlur={() => { if ((reason.trim() || null) !== customer.holdReason) save.mutate({ holdReason: reason.trim() || null }); }} />
        </label>
      ) : null}
      {customer.balance !== null && customer.creditLimit !== null ? (
        <p className={customer.balance > customer.creditLimit ? 'field-error' : 'muted'}>
          {t('balance')} {formatMoney(customer.balance, intlLocale)} / {formatMoney(customer.creditLimit, intlLocale)}
        </p>
      ) : null}
      {save.error ? <ErrorBox error={save.error} /> : null}
    </section>
  );
}
